import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';

interface MultiComboboxProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
}

type PanelPosition = {
  left: number;
  top?: number;
  bottom?: number;
  width: number;
  maxHeight: number;
};

/**
 * MultiCombobox — поле формы с множественным выбором из справочника: выбранные
 * значения показываются чипами внутри поля, список фильтруется по вводу.
 * Отличается от FilterDropdown тем, что это именно поле ввода в форме, а не
 * кнопка-фильтр тулбара. Панель рендерится порталом — чтобы не обрезалась
 * прокруткой модального окна.
 */
export function MultiCombobox({
  values,
  onChange,
  options,
  placeholder = 'Выберите значения',
  disabled,
  className,
  id,
  'aria-label': ariaLabel,
}: MultiComboboxProps) {
  const reactId = useId();
  const inputId = id ?? `multi-combobox-${reactId}`;
  const listboxId = `${inputId}-listbox`;
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const [position, setPosition] = useState<PanelPosition | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => option.toLowerCase().includes(q));
  }, [options, query]);

  const updatePosition = () => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.min(Math.max(rect.width, 200), viewportWidth - 16);
    const left = Math.min(Math.max(8, rect.left), Math.max(8, viewportWidth - width - 8));
    const spaceBelow = viewportHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const placeAbove = spaceBelow < 200 && spaceAbove > spaceBelow;
    const available = Math.max(120, placeAbove ? spaceAbove - 4 : spaceBelow - 4);
    setPosition({
      left,
      width,
      maxHeight: Math.min(288, available),
      ...(placeAbove ? { bottom: viewportHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const handler = () => updatePosition();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, values.length]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const openMenu = () => {
    if (disabled) return;
    setHighlighted(0);
    setOpen(true);
  };

  const toggle = (option: string) => {
    onChange(
      values.includes(option)
        ? values.filter((value) => value !== option)
        : [...values, option],
    );
    setQuery('');
    inputRef.current?.focus();
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) openMenu();
      else setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (open && filtered[highlighted]) toggle(filtered[highlighted]);
    } else if (event.key === 'Backspace' && !query && values.length > 0) {
      onChange(values.slice(0, -1));
    } else if (event.key === 'Escape') {
      // Закрытый список Esc не трогает: его получит окно вокруг. Открытый — закрывается и гасит событие.
      if (!open) return;
      event.preventDefault();
      setOpen(false);
      setQuery('');
    }
  };

  const panel =
    open && position
      ? createPortal(
          <div
            ref={panelRef}
            id={listboxId}
            role="listbox"
            aria-multiselectable="true"
            className={cn(
              'fixed z-[80] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg',
              'dark:border-zinc-800 dark:bg-zinc-900',
            )}
            style={{
              left: position.left,
              top: position.top,
              bottom: position.bottom,
              width: position.width,
              maxHeight: position.maxHeight,
            }}
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400 dark:text-zinc-500">
                Ничего не найдено
              </div>
            ) : (
              filtered.map((option, index) => {
                const selected = values.includes(option);
                const isHighlighted = index === highlighted;
                return (
                  <button
                    key={option}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onMouseEnter={() => setHighlighted(index)}
                    onClick={() => toggle(option)}
                    className={cn(
                      'flex w-full items-start gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                      selected
                        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300'
                        : 'text-gray-700 dark:text-zinc-200',
                      isHighlighted && !selected && 'bg-gray-50 dark:bg-zinc-800/70',
                    )}
                  >
                    <span className="min-w-0 flex-1 whitespace-normal break-words">{option}</span>
                    {selected && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <div
        onClick={openMenu}
        className={cn(
          'flex min-h-[2.375rem] w-full flex-wrap items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 pr-8 text-sm shadow-sm transition-colors',
          'focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500',
          disabled
            ? 'cursor-not-allowed bg-gray-100 dark:bg-zinc-800'
            : 'cursor-text',
          'dark:border-zinc-700 dark:bg-zinc-900',
        )}
      >
        {values.map((value) => (
          <span
            key={value}
            className="inline-flex max-w-full items-center gap-1 rounded bg-indigo-50 px-1.5 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300"
          >
            <span className="truncate">{value}</span>
            {!disabled && (
              <button
                type="button"
                tabIndex={-1}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onChange(values.filter((item) => item !== value));
                }}
                className="rounded text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-200"
                aria-label={`Убрать «${value}»`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          disabled={disabled}
          value={query}
          placeholder={values.length === 0 ? placeholder : ''}
          onChange={(event) => {
            setQuery(event.target.value);
            setHighlighted(0);
            if (!open) setOpen(true);
          }}
          onFocus={openMenu}
          onKeyDown={handleKeyDown}
          className={cn(
            'min-w-[6rem] flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 outline-none',
            'disabled:cursor-not-allowed disabled:text-gray-400',
            'dark:text-zinc-100 dark:placeholder-zinc-500',
          )}
        />
      </div>
      <ChevronDown
        className={cn(
          'pointer-events-none absolute right-2 top-2.5 h-4 w-4 text-gray-400 transition-transform dark:text-zinc-500',
          open && 'rotate-180',
        )}
      />
      {panel}
    </div>
  );
}

export default MultiCombobox;
